import React, { createContext, useContext } from 'react';
import { StateProgramFacade } from './StateProgramFacade';
import { getProgramActiveVersion, getProgramMetadata } from '@plentifulapp/plentiful-program-configuration';
import { WizardEngine } from '@plentifulapp/plentiful-wizard-engine';
import { usePlentifulServiceContext } from '../services/PlentifulServiceContext';

/**
 * Context provider vends services or the error that causes them not to be used.
 * Visible for testing. Avoid using directly.
 * Prefer to use {@link StateProgramContextProvider}
 * and {@link useStateProgramContext} instead.
 */
export const StateProgramContext = createContext<StateProgramFacade | Error>(
  new Error('StateProgramContext must be provided before using context')
);

/**
 * Custom hook to provide StateProgramServices based on context.
 * Wraps built-in hook to ensure that context has been provided.
 */
export const useStateProgramContext = (): StateProgramFacade => {
  const context = useContext(StateProgramContext);
  if (context instanceof Error) {
    throw context;
  }
  return context;
};

/**
 * Props for providing state program services.
 */
interface StateProgramContextProviderProps {
  /**
   * State program ID that is being provided. Required.
   */
  programId: string;
  /**
   * Factory method for building the state program services.
   * Typically in production should be {@link StateProgramFacade#buildForProgram}.
   */
  programServicesFactory: typeof StateProgramFacade.buildForProgram;
  /**
   * Optional child nodes that will use the context.
   */
  children?: React.ReactNode;
}

/**
 * Provider to state program services.
 * Wraps built-in provider to ensure that program services
 * can be loaded before being used.
 */
export const StateProgramContextProvider = function (props: StateProgramContextProviderProps) {
  const serviceRegistry = usePlentifulServiceContext();
  let programServices: StateProgramFacade | Error;

  try {
    programServices = props.programServicesFactory(
      props.programId,
      // provide the bundled dependencies for the state programs directly here
      getProgramMetadata,
      getProgramActiveVersion,
      WizardEngine,
      // provide the remote dependencies from the environment context
      serviceRegistry.attestationFacade
    );
  } catch (cause) {
    // save the error but don't throw it until context is used
    programServices = new Error(
      `State program [${props.programId}] threw exception when loading program services`,
      { cause }
    );
  }

  return (
    <StateProgramContext.Provider value={programServices}>{props.children}</StateProgramContext.Provider>
  );
};
